import React from 'react'

import ErrorMessage from '../errors/ErrorMessage'
import VoiceRecorder from '../audio/VoiceRecorder'
import BoxProgress from './BoxProgress'
import { invalidateInterviewCache, useAudioInterviewSocketUrl } from '../../pages/studio-ai/hooks'
import Loading from '../layout/Loading'
import useSWRSubscription from 'swr/subscription'
import { InterviewStatus, AUDIO_STREAM, InterviewState } from '../../../api/gpt/types'
import LoadingMessages from './LoadingMessages'
import { ANALYTICS_EVENTS, useAnalytics } from '../../../libraries/posthog'
import PlayAudioManager from '../audio/PlayAudioManager'
import AiAvatar from './AiAvatar'

export interface AiInterviewMetrics {
  status: 'success' | 'error'
  time: number // ms
  sessionId: string
}
type QuestionBoxProps = {
  interviewStatus: InterviewStatus
}
const AudioQuestionBox: React.FC<QuestionBoxProps> = ({ interviewStatus }) => {
  const { trackEvent } = useAnalytics()
  // Timer for measuring answer response time
  const answerTimerRef = React.useRef<number | null>(null)
  const socketRef = React.useRef<WebSocket | null>(null)
  const socketMsgHandlerRef = React.useRef<((data: unknown, error?: unknown) => void) | null>(null)
  const playAudioManagerRef = React.useRef<PlayAudioManager>(new PlayAudioManager())
  const { session_id: sessionId, current_question: question } = interviewStatus
  const { data: wsUrl, isLoading: loadingWsUrl, error: errorGettingWsUrl } = useAudioInterviewSocketUrl(sessionId)

  const { data: socketData, error } = useSWRSubscription(wsUrl, (_, { next }) => {
    socketMsgHandlerRef.current = next

    return () => {
      console.log('🧹 Cleaning up WebSocket connection')
      if(socketRef.current) {
        socketRef.current.close()
      }
    }
  })

  const [evaluating, setEvaluating] = React.useState(false)
  const [currentInterview, setCurrentInterview] = React.useState<InterviewStatus | null>(null)
  const [readyToRecord, setReadyToRecord] = React.useState(false)
  const [socketError, setSocketError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if(currentInterview?.session_id !== interviewStatus?.session_id) {
      setCurrentInterview(interviewStatus)
    }
  }, [interviewStatus])


  React.useEffect(() => {
    if(socketData) {
      const { type, status, error } = socketData
      if (type === AUDIO_STREAM.types.SEND) {
        trackAnswerTime(status)
      }
      if(type === AUDIO_STREAM.types.START && status === AUDIO_STREAM.status.SUCCESS) {
        setReadyToRecord(true)
        playAudioManagerRef.current.reset()
      } else if (type === AUDIO_STREAM.types.SEND && status === AUDIO_STREAM.status.SUCCESS) {
        setEvaluating(false)
        const latestInterviewStatus = socketData.data

        // Update interview state immediately so the new question shows
        setCurrentInterview(latestInterviewStatus)
        if(latestInterviewStatus.state !== InterviewState.COMPLETED) {
          // Play audio of follow up question
          playAudioManagerRef.current.markAllAudioReceived()
          playAudioManagerRef.current.play()
        } else {
          // interview done reset playmanager and invalidate cache to fetch inteview result
          playAudioManagerRef.current.reset()
          invalidateInterviewCache()
        }
      } else if (status === AUDIO_STREAM.status.ERROR) {
        setEvaluating(false)
        setReadyToRecord(false)
        setSocketError(error)
      } else if(type === AUDIO_STREAM.types.AUDIO_DELTA) {
        playAudioManagerRef.current.addAudioToQueue(socketData.data)
      }
    }
  }, [socketData])

  const trackAnswerTime = (status: 'success' | 'error') => {
    if (answerTimerRef.current) {
      const responseTime = Date.now() - answerTimerRef.current
      trackEvent(ANALYTICS_EVENTS.AI_INTERVIEW_NEW_QUESTION, { status, time: responseTime, sessionId })
      answerTimerRef.current = null // Reset timer
    }
  }

  /**
   * Calculates the interview progress percentage.
   * Progress is based on the number of main questions asked and follow-ups asked for the current main question,
   * relative to the total possible questions (total main questions * max follow-ups per main question)).
   * @param status The current interview status containing progress details.
   * @returns The progress percentage (0-100), or 0 if status is unavailable or total questions is zero.
   */
  const calculateInterviewProgress = (): number => {
    if (!currentInterview) {
      return 0
    }

    const {
      main_questions_asked,
      total_main_questions,
      follow_ups_asked_this_main,
      max_follow_ups_per_main
    } = currentInterview

    const totalPotentialSteps = total_main_questions * max_follow_ups_per_main

    if (totalPotentialSteps === 0) {
      return 0 // Avoid division by zero
    }

    const completedSteps = (main_questions_asked - 1) * max_follow_ups_per_main + follow_ups_asked_this_main

    // Ensure progress doesn't exceed 100 due to potential state inconsistencies
    const progress = Math.min((completedSteps / totalPotentialSteps) * 100, 100)

    return progress
  }

  if (!question) {
    return <ErrorMessage message='No question available' />
  }

  const connectToWebsocket = () => {
    console.log('🔌 Attempting WebSocket connection')
    return new Promise((resolve, reject) => {
      if(!wsUrl) {
        reject('Cant connect to socket, url is undefined')
        return
      }

      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.addEventListener('open', () => {
        console.log('✅ WebSocket connection opened successfully')
        setSocketError(null)
        resolve(true)
      })

      socket.addEventListener('message', (event) => {
        socketMsgHandlerRef.current ? socketMsgHandlerRef.current(null, JSON.parse(event.data)) : reject('Socket msg handler is null')
      })

      socket.addEventListener('error', (event) => {
        reject(`WebSocket error: ${JSON.stringify(event)}`)
        socketMsgHandlerRef.current ? socketMsgHandlerRef.current(event) : reject('Socket msg handler is null')
      })
    })
  }

  const onRecord = async () => {
    try {
      await connectToWebsocket()
    } catch(error: unknown) {
      if(error instanceof Error) {
        setSocketError(error.message)
      } else {
        setSocketError('Unknown erorr connecting/starting socket')
       }
    }
  }

  const onAudioChunk = (audioBase64: string) => {
    if(!evaluating) {
      if(socketRef.current) {
        socketRef.current.send(JSON.stringify({ type: AUDIO_STREAM.types.AUDIO, chunk_base64: audioBase64 }))
      } else {
        throw new Error('Socket not connected')
      }
    } else {
      // ignore audio we are processing last answer
    }
  }

  const onSend = () => {
    if(socketRef.current) {
      setEvaluating(true)
      // Start metric time
      answerTimerRef.current = Date.now()
      socketRef.current.send(JSON.stringify({ type: AUDIO_STREAM.types.SEND }))
      setReadyToRecord(false)
    } else {
      throw new Error('Socket not connected')
    }
  }

  const onDiscard = () => {
    setReadyToRecord(false)
    if(socketRef.current) {
      socketRef.current.send(JSON.stringify({ type: AUDIO_STREAM.types.DISCARD }))
      socketRef.current.close()
    } else {
      throw new Error('Cant discard, socket not connected')
    }
  }

  if(loadingWsUrl) { return <Loading /> }

  return (
  <BoxProgress progress={calculateInterviewProgress()}>
    <div className='text-center min-h-40 w-full flex flex-col-reverse sm:flex-row sm:gap-4'>
      {/* User Box */}
      <div className='border-2 border-solid border-deep-green-200 rounded-md p-4 sm:my-4 min-w-[400px] relative'>
        <VoiceRecorder
          readyToRecord={readyToRecord}
          isProcessingAudio={evaluating}
          size='lg'
          onRecord={onRecord}
          onAudioChunk={onAudioChunk}
          onSend={onSend}
          onDiscard={onDiscard}
        />
      </div>
      {/* AI Box */}
      <div className='border-2 border-solid border-deep-green-200 rounded-md p-4 my-4 flex-1'>
        <div className='flex justify-center mb-6'><AiAvatar /></div>
        {evaluating
          ? <div className='items-center flex self-center'><LoadingMessages interviewStatus={currentInterview?.state} /></div>
          : <p className='text-md font-bold'>{currentInterview?.current_question}</p>
        }
      </div>
      {(errorGettingWsUrl || error) && <ErrorMessage error={errorGettingWsUrl || error} />}
    </div>
    {socketError && <ErrorMessage message={socketError} />}
  </BoxProgress>
  )
}

export default AudioQuestionBox
